import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  Alert,
  Button,
  DrawerBody,
  DrawerFooter,
  Typography,
  Link,
} from '@one-thd/sui-atomic-components';
import { InlineProjectCreation } from '@thd-olt-component-react/project-forms';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { AddToProductDetails } from '../../common/AddToProductDetails/AddToProductDetails';
import { CopyListTile } from '../copy-list/CopyListTile';
import { TRACK } from '../../AddToConstants';
import { triggerAnalytics } from '../../AddToHelpers';

export const ZeroProjectState = ({
  products,
  singleProduct,
  multipleProduct,
  onProjectCreatedAndItemAdded,
  basePrice
}) => {
  const {
    handleClose
  } = useContext(AddToCoreContext);

  const [showSuccess, setShowSuccess] = useState(false);
  const [createdProjectName, setCreatedProjectName] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState('');

  const fsCopyFromListProjectEnabled = useConfigService('fs:isCopyFromListProjectEnabled');

  const handleInlineCreationSuccess = ({ projectId, projectName }) => {
    setShowSuccess(true);
    setCreatedProjectName(projectName);
    setCreatedProjectId(projectId);

    triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
      name: 'new project project add',
      location: '',
      pageOnly: true,
      items: products.map((prod) => ({
        quantity: prod.quantity || 1,
        price: basePrice || prod.pricing?.value || prod.price,
        sku: prod.itemId,
        aiQuantityEstimate: prod?.aiQuantityEstimate ? 'yes' : 'no'
      })),
      projectId,
      uniqueSkuCount: new Set(products.map((p) => p.itemId)).size,
      projectAddTotal: products.reduce(
        (total, prod) => total + ((basePrice || prod.pricing?.value || prod.price || 0) * (prod.quantity || 1)), 0
      ),
    });

    if (onProjectCreatedAndItemAdded) {
      onProjectCreatedAndItemAdded({ projectId, projectName });
    }
  };

  const handleFooterClick = () => {
    handleClose();
  };

  const projectUrl = createdProjectId ? `/projects/${createdProjectId}` : '#';

  if (showSuccess) {
    return (
      <>
        <DrawerBody>
          <div className="sui-mb-8">
            <Alert status="success" size="large" onClose={() => setShowSuccess(false)}>
              <Typography>
                <Link
                  href={projectUrl}
                  weight="bold"
                  title={createdProjectName}
                >
                  {createdProjectName}
                </Link>
                {' '}successfully created and your Materials have been added to your project
              </Typography>
            </Alert>
          </div>

          <div>
            <AddToProductDetails products={products} />
          </div>
        </DrawerBody>

        <DrawerFooter
          style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / .375)' }}
        >
          <Button
            variant="primary"
            fullWidth
            onClick={handleFooterClick}
            data-testid="zero-state-footer-button"
          >
            Close
          </Button>
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerBody>
        {/* Product Pod - uses existing AddToProductDetails component */}
        <div className="sui-mb-5">
          <AddToProductDetails products={products} />
        </div>

        {/* Inline Project Creation - TextField + Create Button */}
        <InlineProjectCreation
          products={products}
          singleProduct={singleProduct}
          multipleProduct={multipleProduct}
          onSuccess={handleInlineCreationSuccess}
        />

        {/* Copy From Lists Tile - behind feature switch */}
        {fsCopyFromListProjectEnabled && (
          <>
            <div className="sui-pt-3 sui-font-bold">-or-</div>

            <CopyListTile isEnabled />
          </>
        )}
      </DrawerBody>

      <DrawerFooter
        style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / .375)' }}
      >
        <Button
          variant="secondary"
          fullWidth
          onClick={handleFooterClick}
          data-testid="zero-state-footer-button"
        >
          Cancel
        </Button>
      </DrawerFooter>
    </>
  );
};

ZeroProjectState.displayName = 'ZeroProjectState';

ZeroProjectState.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  singleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  multipleProduct: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    sku: PropTypes.string
  })),
  onProjectCreatedAndItemAdded: PropTypes.func,
  basePrice: PropTypes.number
};

ZeroProjectState.defaultProps = {
  products: [],
  singleProduct: [],
  multipleProduct: [],
  onProjectCreatedAndItemAdded: () => { },
  basePrice: null
};

ZeroProjectState.dataModel = extend(
  AddToProductDetails,
  CopyListTile,
  InlineProjectCreation
);
